/**
 * Power Line - Animated Mission Timeline
 *
 * A sci-fi styled status stepper with:
 * - Glowing neon nodes for each status
 * - Energy flow animation through completed segments
 * - Breathing pulse on current step
 * - Elapsed time display between phases
 * - SLA countdown with threat coloring
 */

import React, { useMemo } from 'react';
import { Box, Typography, Stack, keyframes } from '@mui/material';
import {
  ShoppingBag as PlacedIcon,
  Inventory2 as PackedIcon,
  LocalShipping as DispatchedIcon,
  Navigation as TrackingIcon,
  Place as ArrivedIcon,
  CheckCircle as DeliveredIcon,
  Cancel as CancelledIcon,
  Replay as ReturnedIcon,
} from '@mui/icons-material';
import { useMissionTheme } from '../theme';
import { SHOW_SLA } from '../../../config/features';

// Color-independent keyframe animations
const pulse = keyframes`
  0%, 100% { opacity: 1; }
  50% { opacity: 0.6; }
`;

const flowEnergy = keyframes`
  0% { background-position: -100% center; }
  100% { background-position: 200% center; }
`;

// Color-dependent keyframe factories
function makeBreathe(plGlow: string) {
  return keyframes`
    0%, 100% {
      transform: scale(1);
      box-shadow: 0 0 20px ${plGlow};
    }
    50% {
      transform: scale(1.1);
      box-shadow: 0 0 30px ${plGlow}, 0 0 60px ${plGlow};
    }
  `;
}

function makeCompletedGlow(successGlow: string) {
  return keyframes`
    0%, 100% { box-shadow: 0 0 10px ${successGlow}; }
    50% { box-shadow: 0 0 20px ${successGlow}; }
  `;
}

// Timeline steps configuration.
// Labels mirror the status keys one-for-one — the 'tracking' node used to read
// "In Transit", which meant the timeline and the StatusPipeline advance control
// named the same phase two different things.
const TIMELINE_STEPS = [
  { key: 'placed', label: 'Placed', icon: PlacedIcon },
  { key: 'packed', label: 'Packed', icon: PackedIcon },
  { key: 'dispatched', label: 'Dispatched', icon: DispatchedIcon },
  { key: 'tracking', label: 'Tracking', icon: TrackingIcon },
  { key: 'arrived', label: 'Arrived', icon: ArrivedIcon },
  { key: 'delivered', label: 'Delivered', icon: DeliveredIcon },
];

const STATUS_ORDER = ['placed', 'packed', 'dispatched', 'tracking', 'arrived', 'delivered'];

interface PowerLineProps {
  status: string;
  createdAt?: string;
  packedAt?: string;
  dispatchedAt?: string;
  deliveredAt?: string;
  cancellationReason?: string;
  returnReason?: string;
  slaMinutes?: number; // Default SLA target
}

export default function PowerLine({
  status,
  createdAt,
  packedAt,
  dispatchedAt,
  deliveredAt,
  cancellationReason,
  returnReason,
  slaMinutes = 45,
}: PowerLineProps) {
  const { mc, pl, sla } = useMissionTheme();

  // Build color-dependent keyframes (memoized per theme mode)
  const breathe = useMemo(() => makeBreathe(pl.glow), [pl.glow]);
  const completedGlow = useMemo(() => makeCompletedGlow(mc.successGlow), [mc.successGlow]);

  const normalizedStatus = status?.toLowerCase() || 'placed';
  const currentIndex = STATUS_ORDER.indexOf(normalizedStatus);
  const isCancelled = normalizedStatus === 'cancelled';
  const isReturned = normalizedStatus === 'returned';
  const isCompleted = normalizedStatus === 'delivered';

  // Calculate elapsed times
  const timestamps = useMemo(() => {
    const times: Record<string, Date | null> = {
      placed: createdAt ? new Date(createdAt) : null,
      packed: packedAt ? new Date(packedAt) : null,
      dispatched: dispatchedAt ? new Date(dispatchedAt) : null,
      delivered: deliveredAt ? new Date(deliveredAt) : null,
    };
    return times;
  }, [createdAt, packedAt, dispatchedAt, deliveredAt]);

  // Calculate SLA status
  const slaStatus = useMemo(() => {
    if (isCompleted || isCancelled || isReturned) return null;
    if (!createdAt) return null;

    const elapsed = (Date.now() - new Date(createdAt).getTime()) / 60000; // minutes
    const remaining = slaMinutes - elapsed;

    if (remaining <= 0) return { level: 'critical', remaining: 0 };
    if (remaining <= 5) return { level: 'critical', remaining };
    if (remaining <= 10) return { level: 'warning', remaining };
    if (remaining <= 15) return { level: 'watch', remaining };
    return { level: 'safe', remaining };
  }, [createdAt, slaMinutes, isCompleted, isCancelled, isReturned]);

  // Format elapsed time between steps
  const getElapsedTime = (fromKey: string, toKey: string): string | null => {
    const from = timestamps[fromKey];
    const to = timestamps[toKey];
    if (!from || !to) return null;

    const diffMs = to.getTime() - from.getTime();
    const diffMins = Math.floor(diffMs / 60000);

    if (diffMins < 60) return `${diffMins}m`;
    const hours = Math.floor(diffMins / 60);
    const mins = diffMins % 60;
    return `${hours}h ${mins}m`;
  };

  // Format time
  const formatTime = (date: Date | null): string => {
    if (!date) return '';
    return date.toLocaleTimeString('en-ZA', { hour: '2-digit', minute: '2-digit' });
  };

  // Render cancelled/returned state
  if (isCancelled || isReturned) {
    const reason = isCancelled ? cancellationReason : returnReason;
    return (
      <Box
        sx={{
          p: 3,
          background: `linear-gradient(135deg, ${mc.error}20, ${mc.surface})`,
          borderRadius: 3,
          border: `2px solid ${mc.error}`,
          boxShadow: `0 0 30px ${mc.accentGlow}`,
        }}
      >
        <Stack direction="row" alignItems="center" spacing={2}>
          <Box
            sx={{
              width: 56,
              height: 56,
              borderRadius: '50%',
              bgcolor: mc.error,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              animation: `${pulse} 2s infinite`,
            }}
          >
            {isCancelled ? (
              <CancelledIcon sx={{ fontSize: 32, color: 'white' }} />
            ) : (
              <ReturnedIcon sx={{ fontSize: 32, color: 'white' }} />
            )}
          </Box>
          <Box>
            <Typography variant="h5" sx={{ color: mc.error, fontWeight: 700 }}>
              MISSION {isCancelled ? 'ABORTED' : 'RETURNED'}
            </Typography>
            {reason && (
              <Typography variant="body2" sx={{ color: mc.textSecondary, mt: 0.5 }}>
                {reason}
              </Typography>
            )}
          </Box>
        </Stack>
      </Box>
    );
  }

  return (
    <Box sx={{ py: 3 }}>
      {/* SLA Countdown Banner */}
      {SHOW_SLA && slaStatus && (
        <Box
          sx={{
            mb: 3,
            p: 1.5,
            borderRadius: 2,
            bgcolor: sla[slaStatus.level as keyof typeof sla].bg,
            border: `1px solid ${sla[slaStatus.level as keyof typeof sla].border}`,
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            gap: 2,
          }}
        >
          <Typography
            variant="caption"
            sx={{
              color: sla[slaStatus.level as keyof typeof sla].text,
              fontWeight: 600,
              letterSpacing: 1,
            }}
          >
            SLA TARGET
          </Typography>
          <Typography
            variant="h6"
            sx={{
              color: sla[slaStatus.level as keyof typeof sla].text,
              fontFamily: 'monospace',
              fontWeight: 700,
            }}
          >
            {slaStatus.remaining > 0
              ? `${Math.floor(slaStatus.remaining)}:${String(Math.floor((slaStatus.remaining % 1) * 60)).padStart(2, '0')}`
              : 'BREACHED'}
          </Typography>
        </Box>
      )}

      {/* Power Line */}
      <Box sx={{ position: 'relative', px: 2 }}>
        {/* Background Line */}
        <Box
          sx={{
            position: 'absolute',
            top: 28,
            left: 48,
            right: 48,
            height: 4,
            bgcolor: pl.inactive,
            borderRadius: 2,
          }}
        />

        {/* Active Line with Energy Flow */}
        <Box
          sx={{
            position: 'absolute',
            top: 28,
            left: 48,
            width: `calc(${(currentIndex / (TIMELINE_STEPS.length - 1)) * 100}% - ${currentIndex === TIMELINE_STEPS.length - 1 ? 0 : 48}px)`,
            height: 4,
            borderRadius: 2,
            background: isCompleted
              ? mc.success
              : `linear-gradient(90deg,
                  ${mc.success} 0%,
                  ${mc.secondary} 50%,
                  ${mc.success} 100%)`,
            backgroundSize: '200% 100%',
            animation: isCompleted ? 'none' : `${flowEnergy} 2s linear infinite`,
            boxShadow: `0 0 10px ${mc.successGlow}`,
          }}
        />

        {/* Steps */}
        <Stack direction="row" justifyContent="space-between" sx={{ position: 'relative' }}>
          {TIMELINE_STEPS.map((step, index) => {
            const Icon = step.icon;
            const isStepCompleted = index < currentIndex;
            const isCurrent = index === currentIndex;
            const isPending = index > currentIndex;
            const timestamp = timestamps[step.key];

            // Get elapsed time from previous step
            const prevStepKey = index > 0 ? TIMELINE_STEPS[index - 1].key : null;
            const elapsedFromPrev = prevStepKey ? getElapsedTime(prevStepKey, step.key) : null;

            return (
              <Box
                key={step.key}
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  flex: 1,
                  position: 'relative',
                }}
              >
                {/* Elapsed time badge */}
                {elapsedFromPrev && index > 0 && (
                  <Box
                    sx={{
                      position: 'absolute',
                      top: 20,
                      left: '-50%',
                      transform: 'translateX(50%)',
                      px: 1,
                      py: 0.25,
                      bgcolor: mc.elevated,
                      borderRadius: 1,
                      border: `1px solid ${mc.border}`,
                      zIndex: 2,
                    }}
                  >
                    <Typography
                      variant="caption"
                      sx={{
                        fontSize: '0.6rem',
                        color: mc.textMuted,
                        fontFamily: 'monospace',
                      }}
                    >
                      {elapsedFromPrev}
                    </Typography>
                  </Box>
                )}

                {/* Node */}
                <Box
                  sx={{
                    width: 56,
                    height: 56,
                    borderRadius: '50%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    bgcolor: isPending
                      ? mc.surface
                      : isCurrent
                        ? mc.accent
                        : mc.success,
                    border: `3px solid ${
                      isPending
                        ? pl.inactive
                        : isCurrent
                          ? mc.accent
                          : mc.success
                    }`,
                    transition: 'all 0.3s ease',
                    animation: isCurrent
                      ? `${breathe} 2s ease-in-out infinite`
                      : isStepCompleted
                        ? `${completedGlow} 3s ease-in-out infinite`
                        : 'none',
                    zIndex: 1,
                  }}
                >
                  <Icon
                    sx={{
                      fontSize: 28,
                      color: isPending ? mc.textMuted : 'white',
                    }}
                  />
                </Box>

                {/* Label */}
                <Typography
                  variant="body2"
                  sx={{
                    mt: 1.5,
                    fontWeight: isCurrent ? 700 : 500,
                    color: isPending
                      ? mc.textMuted
                      : isCurrent
                        ? mc.accent
                        : mc.success,
                    textAlign: 'center',
                  }}
                >
                  {step.label}
                </Typography>

                {/* Timestamp */}
                {timestamp && (
                  <Typography
                    variant="caption"
                    sx={{
                      color: mc.textSecondary,
                      fontFamily: 'monospace',
                      fontSize: '0.7rem',
                    }}
                  >
                    {formatTime(timestamp)}
                  </Typography>
                )}
              </Box>
            );
          })}
        </Stack>
      </Box>
    </Box>
  );
}
